/**
 * Pure helpers for resolving values from `ibeSearchDefaults` (FieldConfig form).
 *
 * Used by both `useFilterInitializer` (full hydration including URL/storage)
 * and `useSearchWidgetDefaults` (defaults-only initialization).
 */

import type { DateRange, RoomOccupancy } from '@/types/SearchBar';
import type { IbeSearchDefaults } from '@/types/SearchDefaults';

/** How many PDP offers to reveal per "page" when no valid config is provided. */
export const DEFAULT_OFFER_PAGE_SIZE = 12;

/** ± offset around the selected duration when no valid config is provided. */
export const DEFAULT_DURATION_FLEX = 4;

/**
 * Resolve the PDP offer-list page size from `ibeSearchDefaults.offerPageSize`.
 * Falls back to {@link DEFAULT_OFFER_PAGE_SIZE} for missing or invalid values.
 */
export function resolveOfferPageSize(defaults: IbeSearchDefaults | null): number {
  const raw = defaults?.offerPageSize;
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0
    ? Math.floor(raw)
    : DEFAULT_OFFER_PAGE_SIZE;
}

/**
 * Resolve the duration widening offset from `ibeSearchDefaults.durationFlex`.
 * Falls back to {@link DEFAULT_DURATION_FLEX} for missing or invalid values.
 *
 * Unlike {@link resolveOfferPageSize} this accepts `0`: it is the documented
 * kill switch for the widening, so it must survive rather than fall back.
 */
export function resolveDurationFlex(defaults: IbeSearchDefaults | null): number {
  const raw = defaults?.durationFlex;
  return typeof raw === 'number' && Number.isFinite(raw) && raw >= 0
    ? Math.floor(raw)
    : DEFAULT_DURATION_FLEX;
}

/**
 * Parse a defaults date string. Accepts `+N` (today + N days) or `YYYY-MM-DD`.
 */
export function parseDefaultDate(dateStr: string): Date | null {
  if (dateStr.startsWith('+')) {
    const days = parseInt(dateStr.substring(1), 10);
    if (Number.isNaN(days)) return null;
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  }
  // Local-midnight parse, consistent with FilterValidation.validateDate — a bare
  // `new Date('YYYY-MM-DD')` parses as UTC midnight, which reads back as the
  // previous day for negative-offset timezones.
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Resolve a `[start, end]` date range from `defaults.date` and `defaults.todate`.
 * Returns null if either side is missing or invalid.
 */
export function parseDatesFromConfig(defaults: IbeSearchDefaults | null): DateRange {
  const dateStr = defaults?.date?.value;
  const todateStr = defaults?.todate?.value;
  if (!dateStr || !todateStr) return null;

  const start = parseDefaultDate(dateStr);
  const end = parseDefaultDate(todateStr);
  if (!start || !end) return null;
  return [start, end];
}

/**
 * Parse `defaults.pax.value` (comma-separated ages, e.g. "35,35,10,3") into a
 * single-room `RoomOccupancy`. Always collapses to one room — multi-room is
 * only supported via the URL `pax` parameter (see `paxParser.ts`).
 *
 * Convention: age ≥ 18 (or exactly 35) → adult, otherwise → child with that age.
 */
export function parsePaxConfigString(paxStr: string): RoomOccupancy | undefined {
  const ages = paxStr
    .split(',')
    .map((age) => parseInt(age, 10))
    .filter((age) => !Number.isNaN(age));

  if (ages.length === 0) return undefined;

  const persons = ages.map((age) =>
    age >= 18 || age === 35 ? { type: 'adult' as const } : { type: 'child' as const, age }
  );

  return { rooms: [{ persons }] };
}

/**
 * Hardcoded last-resort defaults: today + 7 days, two adults in one room.
 */
export function getHardcodedDefaults(): {
  dateRange: [Date, Date];
  roomOccupancy: RoomOccupancy;
  duration: number;
} {
  const today = new Date();
  const futureDate = new Date(today);
  futureDate.setDate(today.getDate() + 7);

  return {
    dateRange: [today, futureDate],
    roomOccupancy: {
      rooms: [{ persons: [{ type: 'adult' }, { type: 'adult' }] }],
    },
    duration: 7,
  };
}
